import { useEffect } from 'react';
import { MotifButton, MotifModal } from '@ey-xd/motif-wc-react';
import { useClientStore } from '@/client/client-store';
import { postClientRefresh } from '@/client/broadcast';
import { getRouterInstance } from '@/router/router-instance';

/**
 * Confirmation gate for a direct / hard-URL navigation that changes the active
 * client (address bar entry, external deep link, same-tab paste). Port of the
 * legacy `BaseRouteGuard` direct-navigation branch
 * (`raptor-shell-ui/src/app/core/guards/base-route.guard.ts`), which popped
 * `EY_RAPToR_URLClientRefreshInTabs` ("ALL OPEN TABS will be refreshed") when
 * the requested URL carried a different clientId than the active one.
 *
 * The pending switch is recorded in `persistLegacyRouteClient` (router.tsx) on a
 * cold load whose persisted client differs from the URL's client. A dropdown
 * switch (own `ClientSwitchModal`) and a Back/Forward switch
 * (`ClientSwitchBackGuard`) never set it, so they are not caught here.
 *
 * This tab has already resolved to the new client, so "Yes" only broadcasts the
 * refresh that moves the OTHER tabs. "No" reverts to the previous client and
 * returns to its home.
 */
export function DirectClientChangeGuard() {
  const pending = useClientStore((s) => s.pendingDirectSwitch);
  const available = useClientStore((s) => s.available);
  const setPendingDirectSwitch = useClientStore((s) => s.setPendingDirectSwitch);
  const resetToPrevious = useClientStore((s) => s.resetToPrevious);
  const setSwitchPopupOpen = useClientStore((s) => s.setSwitchPopupOpen);

  // Only a switch to a client the user can actually access should prompt. A
  // direct URL with an invalid / inaccessible client GUID also records a pending
  // switch, but the client-access gate (router.tsx `clientAccessRedirect`)
  // bounces the user and warns for that case — so showing the "Client Change"
  // confirmation here is wrong (invalid client GUID must not pop the dialog).
  // Resolve the target only when it is in the accessible list.
  const target = pending ? available.find((c) => c.id === pending.to) : undefined;

  // Drop a pending switch that targets an inaccessible client once the client
  // list has loaded, so the popup never renders and the legacy popup-open flag
  // is not left set. While the list is still empty we wait rather than clear, so
  // a valid deep-link switch is not discarded before its client resolves.
  useEffect(() => {
    if (pending && available.length > 0 && !available.some((c) => c.id === pending.to)) {
      setPendingDirectSwitch(null);
    }
  }, [pending, available, setPendingDirectSwitch]);

  // Mirror the legacy `EY_RAPToR_IsClientSwitchPopUpOpen` flag while this popup
  // is open so legacy MFEs / guards that poll it see the state. Only touch the
  // flag when we actually prompt (a valid, accessible target).
  useEffect(() => {
    if (!target) return;
    setSwitchPopupOpen(true);
    return () => setSwitchPopupOpen(false);
  }, [target, setSwitchPopupOpen]);

  if (!pending || !target) return null;

  const confirm = () => {
    postClientRefresh({
      clientId: pending.to,
      previousClientId: pending.from,
      source: 'url-change',
    });
    setPendingDirectSwitch(null);
  };

  const cancel = () => {

    // Revert only when the previous client is still accessible; otherwise stay
    // on the (accessible) URL client rather than stranding the user on a client
    // they can no longer reach.
    if (available.some((c) => c.id === pending.from)) {
      resetToPrevious();
      getRouterInstance()?.history.replace(`/${pending.from}/app-container/home`);
    }
    setPendingDirectSwitch(null);
  };

  return (
    <div className="motif-theme-light">
      <MotifModal
        open
        onModalClose={cancel}
        headerTitle="Client Change"
        size="md"
        showFeaturedIcon={false}
        style={
          {
            '--modal-overflow-y': 'hidden',
            '--modal-body-overflow-y': 'hidden',
            '--modal-footer-width': '100%',
          } as React.CSSProperties
        }
      >
        <p slot="body">
          Please make sure to save any changes before changing the client,{' '}
          <b className="eyfontbold">ALL OPEN TABS</b> will be refreshed. Would you like to proceed?
        </p>
        <div slot="footer" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <MotifButton variant="secondary" onClick={cancel}>
              No
            </MotifButton>
            <MotifButton variant="primary" onClick={confirm}>
              Yes
            </MotifButton>
          </div>
        </div>
      </MotifModal>
    </div>
  );
}
